import { COPY_HISTORY_TABLE_COLUMNS as columns } from "../../../constants/investigationAnalysisReview";

import React, { useState } from "react";
import {
  Box,
  Chip,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import ArrowDropUpIcon from "@mui/icons-material/ArrowDropUp";
import ApplicationStatusChip from "../../../components/StatusChip/ApplicationStatusChip";
import { getApplicationStatusStyle } from "../../../constants/statusStyles";

const statusChipSx = {
  height: 24,
  minWidth: 70,
  border: 0,
  bgcolor: "#E9EAEB",
  color: "#475467",
  "& .MuiChip-label": { px: 1.5, fontSize: 14 },
};

const versionChipSx = {
  height: 24,
  border: "1px solid #D0D5DD",
  bgcolor: "#FFFFFF",
  color: "#175CD3",
  "& .MuiChip-label": { px: 1.5, fontSize: 14 },
};

const getVersionNumber = (item, fallback) => {
  const value =
    item?.version_label ??
    item?.version_number ??
    item?.version ??
    item?.version_no ??
    item?.batch_version;
  if (value === null || value === undefined || value === "") return fallback;
  return String(value).replace(/^version[\s_-]*/i, "");
};

const getVersionSortValue = (item) => {
  const versionNumber = Number(getVersionNumber(item, 0));
  return Number.isFinite(versionNumber) ? versionNumber : 0;
};

const CopyHistoryTable = ({
  records = [],
  page = 1,
  pageSize = 10,
  loading = false,
  showEmptyState = false,
  onView,
}) => {
  const [expandedRow, setExpandedRow] = useState(null);
  const cellSx = { fontSize: 14, py: 1, borderColor: "#EAECF0" };

  const renderHistoryRow = (version, index, versions, rowKey) => {
    const versionNumber = getVersionNumber(
      version,
      Math.max(versions.length - index - 1, 1),
    );
    return (
      <TableRow
        key={`${rowKey}-${version.ingestion_file_batch_id ?? version.ingested_file_id ?? index}`}
      >
        <TableCell sx={cellSx} />
        <TableCell sx={{ ...cellSx, pl: 3 }}>
          <Typography
            component="button"
            type="button"
            onClick={() =>
              onView?.({
                ...version,
                version_number: versionNumber,
              })
            }
            fontSize={14}
            color="#175CD3"
            sx={{ border: 0, bgcolor: "transparent", p: 0, cursor: "pointer" }}
          >
            •&nbsp; {version.batch_id || "-"}
          </Typography>
        </TableCell>
        <TableCell sx={cellSx}>
          <Chip
            label={`Version ${versionNumber}`}
            size="small"
            sx={versionChipSx}
          />
        </TableCell>
        <TableCell sx={cellSx}>{version.template_version ?? "-"}</TableCell>
        <TableCell sx={cellSx}>
          {getApplicationStatusStyle(version.status) ? (
            <ApplicationStatusChip status={version.status} sx={statusChipSx} />
          ) : (
            <Chip label={version.status || "-"} size="small" sx={statusChipSx} />
          )}
        </TableCell>
        <TableCell sx={cellSx}>
          {version.batch_copied_by || version.uploaded_by || "-"}
        </TableCell>
        <TableCell sx={cellSx}>
          {version.batch_copied_date || version.created_at || "-"}
        </TableCell>
      </TableRow>
    );
  };

  return (
    <Box width="100%">
      <TableContainer
        component={Paper}
        sx={{
          boxShadow: "none",
          maxHeight: "calc(100vh - 245px)",
          overflow: "auto",
        }}
      >
        <Table stickyHeader sx={{ tableLayout: "fixed", minWidth: 900 }}>
          <TableHead>
            <TableRow>
              {columns.map((column, index) => (
                <TableCell
                  key={column.label}
                  sx={{
                    width: column.width,
                    bgcolor: "#E9EAEB",
                    color: "#101828",
                    fontSize: 14,
                    fontWeight: 700,
                    py: 1,
                    borderBottom: 0,
                    ...(index === 0 && { borderRadius: "8px 0 0 8px" }),
                    ...(index === columns.length - 1 && {
                      borderRadius: "0 8px 8px 0",
                    }),
                  }}
                >
                  {column.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {records.map((record, index) => {
              const rowKey = `${record.batch_id}-${record.ingestion_file_batch_id ?? record.ingested_file_id ?? index}`;
              const isExpanded = expandedRow === rowKey;
              const versions = record.versions || [];
              const matchingVersion =
                record.ingestion_file_batch_id !== null &&
                record.ingestion_file_batch_id !== undefined
                  ? versions.find(
                      (version) =>
                        String(version.ingestion_file_batch_id) ===
                        String(record.ingestion_file_batch_id),
                    )
                  : null;
              const current =
                matchingVersion ||
                versions.find((version) => version.is_current) ||
                record;
              const currentVersionNumber = getVersionNumber(
                current,
                getVersionNumber(record, Math.max(versions.length, 1)),
              );
              const olderVersions = versions
                .filter((version) => version !== current)
                .sort(
                  (first, second) =>
                    getVersionSortValue(second) - getVersionSortValue(first),
                );
              const srNo = record.sr_no || (page - 1) * pageSize + index + 1;
              return (
                <React.Fragment key={rowKey}>
                  <TableRow hover>
                    <TableCell sx={{ ...cellSx }}>
                      {srNo}
                    </TableCell>
                    <TableCell sx={cellSx}>
                      <Box display="flex" alignItems="center" gap={0.5}>
                        <Typography
                          component="button"
                          type="button"
                          onClick={() =>
                            onView?.({
                              ...record,
                              ...current,
                              version_number: currentVersionNumber,
                            })
                          }
                          fontSize={14}
                          color="#175CD3"
                          noWrap
                          sx={{
                            border: 0,
                            bgcolor: "transparent",
                            p: 0,
                            cursor: "pointer",
                          }}
                        >
                          {record.batch_id || "-"}
                        </Typography>
                        <IconButton
                          size="small"
                          onClick={() =>
                            setExpandedRow(isExpanded ? null : rowKey)
                          }
                          sx={{ p: 0.25 }}
                        >
                          {isExpanded ? (
                            <ArrowDropUpIcon />
                          ) : (
                            <ArrowDropDownIcon />
                          )}
                        </IconButton>
                      </Box>
                    </TableCell>
                    <TableCell sx={cellSx}>
                      <Chip
                        label={`Version ${currentVersionNumber}`}
                        size="small"
                        sx={{ ...versionChipSx, bgcolor: "#EAF2FF", border: 0 }}
                      />
                    </TableCell>
                    <TableCell sx={cellSx}>
                      {current.template_version ??
                        record.template_version ??
                        "-"}
                    </TableCell>
                    <TableCell sx={cellSx}>
                      {getApplicationStatusStyle(current.status) ? (
                        <ApplicationStatusChip status={current.status} sx={statusChipSx} />
                      ) : (
                        <Chip
                          label={current.status || "-"}
                          size="small"
                          sx={statusChipSx}
                        />
                      )}
                    </TableCell>
                    <TableCell sx={cellSx}>
                      {record.batch_copied_by ||
                        current.batch_copied_by ||
                        current.uploaded_by ||
                        "-"}
                    </TableCell>
                    <TableCell sx={cellSx}>
                      {record.batch_copied_date ||
                        current.batch_copied_date ||
                        "-"}
                    </TableCell>
                  </TableRow>
                  {isExpanded &&
                    olderVersions.map((version, versionIndex) =>
                      renderHistoryRow(version, versionIndex, versions, rowKey),
                    )}
                </React.Fragment>
              );
            })}
            {showEmptyState && !loading && !records.length && (
              <TableRow>
                <TableCell colSpan={columns.length} align="center">
                  <Typography fontSize={14}>No copied batches found</Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default CopyHistoryTable;
